/**
 * Client-side gate for the recurring-event form.
 *
 * `api/src/event/service/assert-valid-recurrence.ts` is AUTHORITATIVE — this is
 * a mirror of its accept-set, and it may only ever be *stricter*, never looser.
 * Drifting stricter blocks a save the server would have taken; drifting looser
 * re-opens the raw 400 (or, before that guard existed, the 500) that this form
 * used to hand the admin.
 *
 * Why a mirror is not redundant here: `CreateEventForm` never passed
 * `isSubmitDisabled` to `FormModal`, so every red message in the recurrence
 * block was decoration and the Create button was always live. An admin could
 * tick "recurring", pick no days, and submit. The server now answers that with
 * a 400 — but a toast reading "Creation failed: A recurring event needs at
 * least one day of the week" after a round trip is a worse experience than a
 * disabled button and an inline message, and it is the exact input the form's
 * own UI already knew was wrong.
 *
 * The second job is the occurrence count. The server caps a single request at
 * MAX_RECURRING_OCCURRENCES; without a preview the admin discovers that cap by
 * being rejected. Showing "20 sessions will be created" turns the whole feature
 * from opaque to legible, and it is the only place the number is ever stated.
 */

/** Mirrors MAX_RECURRING_HORIZON_DAYS in the API guard. */
export const MAX_RECURRING_HORIZON_DAYS = 730;

/** Mirrors MAX_RECURRING_OCCURRENCES in the API guard. */
export const MAX_RECURRING_OCCURRENCES = 366;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type RecurrenceErrorCode =
    | 'missing_start_time'
    | 'no_days_selected'
    | 'invalid_day'
    | 'missing_end_date'
    | 'invalid_end_date'
    | 'end_date_before_start'
    | 'horizon_too_long'
    | 'no_occurrences'
    | 'too_many_occurrences';

export type RecurrenceInput = {
    /** The exact `startTime` string the create payload will carry (UTC ISO). */
    startTimeIso: string;
    /** The exact `recurringEndDate` string the payload will carry (UTC ISO). */
    endDateIso: string;
    daysOfWeek: number[];
};

/**
 * Counts the occurrences the API would materialise for this rule.
 *
 * Deliberately replicates `generateRecurringDates`' UTC day-walk rather than
 * approximating with `horizonDays / 7`: the approximation is wrong at both ends
 * (a range starting mid-week, a range ending before that weekday recurs), and a
 * preview that disagrees with the server by one is worse than none.
 *
 * Returns 0 for any input the day-walk cannot run on, so callers must validate
 * before trusting a count of 0 to mean "this rule genuinely produces nothing".
 */
export const countRecurringOccurrences = ({
    startTimeIso,
    endDateIso,
    daysOfWeek,
}: RecurrenceInput): number => {
    const start = new Date(startTimeIso);
    const end = new Date(endDateIso);
    if (
        Number.isNaN(start.getTime()) ||
        Number.isNaN(end.getTime()) ||
        daysOfWeek.length === 0
    ) {
        return 0;
    }

    const cursor = new Date(start);
    cursor.setUTCHours(0, 0, 0, 0);

    // Bound the loop independently of the horizon check so a caller that skips
    // validation cannot spin this for decades.
    const lastDay = Math.min(
        end.getTime(),
        cursor.getTime() + MAX_RECURRING_HORIZON_DAYS * MS_PER_DAY
    );

    let count = 0;
    while (cursor.getTime() <= lastDay) {
        if (daysOfWeek.includes(cursor.getUTCDay())) {
            count++;
        }
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return count;
};

/**
 * @returns the first reason this recurrence would be refused, or `undefined`
 *   when the API would accept it. Total — never throws.
 */
export const validateRecurrence = ({
    startTimeIso,
    endDateIso,
    daysOfWeek,
}: RecurrenceInput): RecurrenceErrorCode | undefined => {
    const start = new Date(startTimeIso);
    if (!startTimeIso || Number.isNaN(start.getTime())) {
        return 'missing_start_time';
    }

    if (!Array.isArray(daysOfWeek) || daysOfWeek.length === 0) {
        return 'no_days_selected';
    }
    const hasInvalidDay = daysOfWeek.some(
        (day) =>
            typeof day !== 'number' ||
            !Number.isInteger(day) ||
            day < 0 ||
            day > 6
    );
    if (hasInvalidDay) {
        return 'invalid_day';
    }

    if (!endDateIso || endDateIso.trim() === '') {
        return 'missing_end_date';
    }
    const end = new Date(endDateIso);
    if (Number.isNaN(end.getTime())) {
        return 'invalid_end_date';
    }

    const startOfStartDay = new Date(start);
    startOfStartDay.setUTCHours(0, 0, 0, 0);

    if (end.getTime() < startOfStartDay.getTime()) {
        return 'end_date_before_start';
    }

    const horizonDays =
        (end.getTime() - startOfStartDay.getTime()) / MS_PER_DAY;
    if (horizonDays > MAX_RECURRING_HORIZON_DAYS) {
        return 'horizon_too_long';
    }

    const occurrences = countRecurringOccurrences({
        startTimeIso,
        endDateIso,
        daysOfWeek,
    });
    if (occurrences === 0) {
        return 'no_occurrences';
    }
    if (occurrences > MAX_RECURRING_OCCURRENCES) {
        return 'too_many_occurrences';
    }

    return undefined;
};
